/**
 * Django admin join before history/send; avoid client joinRoom without invite (403/429).
 * retry-structure: broken structure, forceRepair, or member missing from Synapse after sync.
 */
import { isAxiosError } from 'axios'
import { ref } from 'vue'
import { ChatApiService } from '@/modules/chat-matrix-fast/services/chatApiService'
import { isBackendRoomReady } from '@/modules/chat-matrix-fast/services/chatReadiness'
import {
  clearMatrixRoomHistoryForbidden,
  isMatrixRoomHistoryForbidden,
} from '@/modules/chat-matrix-fast/services/matrixChatClient'
import {
  getMatrixClient,
  startMatrixClient,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'
import { restoreForgottenRoom } from '@/modules/chat-matrix-fast/services/matrixForgottenRooms'
import type { IChat } from '@/modules/chat-matrix-fast/types/IChat'
import { chatDebugLog, eventsDebugLog } from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import { describeEventChatMatrixRoom } from '@/modules/chat-matrix-fast/utils/eventChatDebug'
import { awaitMembershipOperation } from '@/modules/chat-matrix-fast/services/membershipProtocol'
import { clearMembershipStatus, setMembershipStatus } from '@/modules/chat-matrix-fast/services/matrixMembershipState'

const WAIT_ATTEMPTS = 40
const SOFT_WAIT_ATTEMPTS = 10
const WAIT_DELAY_MS = 300
const REPAIR_COOLDOWN_MS = 15_000

/** Composer ready-gate is not Matrix-reactive; bump after join so UI leaves «Подключаем…». */
export const matrixJoinEpoch = ref(0)

/** One in-flight ensure per chat+room (soft and force share the lock). */
const inFlight = new Map<string, Promise<boolean>>()
/** Rooms that need forceRepair while a soft wait is already running. */
const pendingForceRepair = new Set<string>()
const lastRepairAt = new Map<string, number>()

function isRetryStructureUnavailable(error: unknown): boolean {
  return isAxiosError(error) && error.response?.status === 503
}

export type MembershipChatSnapshot = Pick<
  IChat,
  'matrix_room_id' | 'space_room_id' | 'discussion_mode' | 'transition_status'
>

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms))

function isJoined(roomId: string): boolean {
  try {
    const room = getMatrixClient().getRoom(roomId)
    return room?.getMyMembership() === 'join'
  } catch {
    return false
  }
}

/** SDK join без доступа к /messages (403) — membership ещё не починен. */
function isEffectivelyJoined(roomId: string): boolean {
  return isJoined(roomId) && !isMatrixRoomHistoryForbidden(roomId)
}

function membershipOf(roomId: string): string | null {
  try {
    return getMatrixClient().getRoom(roomId)?.getMyMembership() ?? null
  } catch {
    return null
  }
}

function markJoined(roomId: string): true {
  clearMatrixRoomHistoryForbidden(roomId)
  matrixJoinEpoch.value += 1
  return true
}

function isMatrixSyncSettled(): boolean {
  try {
    const state = getMatrixClient().getSyncState()
    return state === 'PREPARED' || state === 'SYNCING' || state === 'CATCHUP'
  } catch {
    return false
  }
}

function canRunRepair(key: string): boolean {
  const last = lastRepairAt.get(key) ?? 0
  return Date.now() - last >= REPAIR_COOLDOWN_MS
}

/** Structure needs Django repair — not a healthy idle room. */
function needsStructureRepair(
  chat: MembershipChatSnapshot | null | undefined,
): boolean {
  if (!chat) {
    return false
  }
  if (chat.transition_status === 'failed') {
    return true
  }
  if (!chat.matrix_room_id?.trim()) {
    return true
  }
  if (chat.discussion_mode === 'topics' && !chat.space_room_id?.trim()) {
    return true
  }
  return chat.transition_status === 'provisioning' && !isBackendRoomReady(chat)
}

export function isMatrixMembershipForbiddenError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false
  }
  const matrixError = error as {
    httpStatus?: number
    statusCode?: number
    errcode?: string
    message?: string
    data?: { errcode?: string }
  }
  const status = matrixError.httpStatus ?? matrixError.statusCode
  const errcode = matrixError.errcode ?? matrixError.data?.errcode
  const message = String(matrixError.message || '').toLowerCase()
  return status === 403
    || errcode === 'M_FORBIDDEN'
    || message.includes('not in room')
    || message.includes('not invited')
}

async function waitUntilJoined(
  roomId: string,
  attempts: number,
): Promise<boolean> {
  const matrixClient = await startMatrixClient()
  let inviteJoinAttempted = false

  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (isEffectivelyJoined(roomId)) {
      return markJoined(roomId)
    }

    // Soft client-side join only when Synapse already invited us.
    if (!inviteJoinAttempted && membershipOf(roomId) === 'invite') {
      inviteJoinAttempted = true
      try {
        await matrixClient.joinRoom(roomId)
      } catch {
        // Admin/Django join remains the primary path.
      }
      if (isEffectivelyJoined(roomId)) {
        return markJoined(roomId)
      }
    }

    await sleep(WAIT_DELAY_MS)
  }

  return isEffectivelyJoined(roomId) ? markJoined(roomId) : false
}

async function runRetryStructure(
  chatId: number | string,
  roomId: string,
  chat: MembershipChatSnapshot | null | undefined,
  retry = false,
): Promise<boolean> {
  const client = await startMatrixClient()
  const userId = client.getUserId?.()
  try {
    if (needsStructureRepair(chat)) {
      await ChatApiService.retryStructure(chatId)
    }
    const confirmed = await awaitMembershipOperation({
      request: () => ChatApiService.requestMembership(chatId, roomId, retry),
      read: () => ChatApiService.fetchMembershipStatus(chatId, roomId),
      onStatus: state => setMembershipStatus(roomId, state, userId),
      sleep,
    })
    if (!confirmed) {
      return false
    }
    clearMatrixRoomHistoryForbidden(roomId)
    const joined = await waitUntilJoined(roomId, WAIT_ATTEMPTS)
    return joined ? markJoined(roomId) : false
  } catch (error) {
    if (!isAxiosError(error) || ![404, 405].includes(error.response?.status ?? 0)) {
      setMembershipStatus(roomId, { status: 'failed', error_code: isAxiosError(error) ? `http_${error.response?.status ?? 0}` : 'network' }, userId)
      if (isRetryStructureUnavailable(error)) {
        throw error
      }
      return false
    }
    // Older portals keep their existing repair contract during staged rollout.
    clearMembershipStatus(roomId, userId)
  }
  chatDebugLog('ensureMatrixChatMembership → retryStructure', {
    chatId,
    roomId,
    forceRepair: true,
    transition: chat?.transition_status ?? null,
  })

  try {
    await ChatApiService.retryStructure(chatId)
  } catch (error) {
    chatDebugLog('ensureMatrixChatMembership → retryStructure failed', {
      chatId,
      roomId,
      error: error instanceof Error ? error.message : String(error),
    })
    if (isRetryStructureUnavailable(error)) {
      throw error
    }
  }

  // 403 /messages ставит forbidden; без сброса waitUntilJoined ждёт 12с впустую.
  clearMatrixRoomHistoryForbidden(roomId)
  const joined = await waitUntilJoined(roomId, WAIT_ATTEMPTS)
  chatDebugLog('ensureMatrixChatMembership → done (repair)', {
    chatId,
    roomId,
    joined,
  })
  if (joined) {
    markJoined(roomId)
  }
  return joined
}

/**
 * Idempotent: if already joined — no-op.
 * Django retry-structure joins the caller when the room is missing from the SDK.
 */
export async function ensureMatrixChatMembership(options: {
  chatId: number | string
  roomId: string
  /** Chat snapshot — skip repair when idle + room (+ space for topics). */
  chat?: MembershipChatSnapshot | null
  /** Force Django join/repair (send 403, outbox, topic satellite rooms). */
  forceRepair?: boolean
  softOnly?: boolean
  /** Для eventsDebugLog при lazy-join чатов задач. */
  isEventChat?: boolean
}): Promise<boolean> {
  const roomId = options.roomId.trim()
  const chatId = options.chatId
  if (!roomId || chatId == null || chatId === '') {
    return false
  }

  await startMatrixClient()
  restoreForgottenRoom(roomId)

  if (isJoined(roomId) && !options.forceRepair) {
    if (isMatrixRoomHistoryForbidden(roomId)) {
      if (options.softOnly) {
        if (options.isEventChat) {
          eventsDebugLog('membership · history forbidden (soft miss)', {
            chatId,
            roomId,
            matrix: describeEventChatMatrixRoom(roomId),
          })
        }
        return false
      }
    } else {
      clearMatrixRoomHistoryForbidden(roomId)
      if (options.isEventChat) {
        eventsDebugLog('membership · already joined', {
          chatId,
          roomId,
          matrix: describeEventChatMatrixRoom(roomId),
        })
      }
      return true
    }
  }

  if (options.softOnly) {
    if (options.isEventChat) {
      eventsDebugLog('membership · soft wait', {
        chatId,
        roomId,
        softOnly: true,
        matrix: describeEventChatMatrixRoom(roomId),
      })
    }
    chatDebugLog('ensureMatrixChatMembership → soft wait', {
      chatId,
      roomId,
      softOnly: true,
      transition: options.chat?.transition_status ?? null,
    })
    const joined = await waitUntilJoined(roomId, SOFT_WAIT_ATTEMPTS)
    chatDebugLog('ensureMatrixChatMembership → done (soft)', {
      chatId,
      roomId,
      joined,
      softOnly: true,
    })
    return joined
  }

  const key = `${chatId}:${roomId}`
  if (options.forceRepair) {
    pendingForceRepair.add(key)
  }

  const existing = inFlight.get(key)
  if (existing) {
    return existing
  }

  const promise = (async () => {
    try {
      const force = Boolean(options.forceRepair)
        || needsStructureRepair(options.chat)
        || pendingForceRepair.has(key)
      // After /sync the room is still absent → Django member is not in Synapse (event chats).
      const missingAfterSync = !membershipOf(roomId) && isMatrixSyncSettled()
      const shouldRepair = (force || missingAfterSync || pendingForceRepair.has(key))
        && canRunRepair(key)

      if (shouldRepair) {
        lastRepairAt.set(key, Date.now())
        pendingForceRepair.delete(key)
        if (options.isEventChat) {
          eventsDebugLog('membership · retry-structure', {
            chatId,
            roomId,
            force,
            missingAfterSync,
            matrix: describeEventChatMatrixRoom(roomId),
          })
        }
        return runRetryStructure(chatId, roomId, options.chat, Boolean(options.forceRepair))
      }

      if (options.isEventChat) {
        eventsDebugLog('membership · soft wait', {
          chatId,
          roomId,
          missingAfterSync,
          matrix: describeEventChatMatrixRoom(roomId),
        })
      }
      chatDebugLog('ensureMatrixChatMembership → soft wait', {
        chatId,
        roomId,
        transition: options.chat?.transition_status ?? null,
        missingAfterSync,
      })
      const joined = await waitUntilJoined(roomId, SOFT_WAIT_ATTEMPTS)
      if (joined) {
        if (options.isEventChat) {
          eventsDebugLog('membership · joined (soft)', {
            chatId,
            roomId,
            matrix: describeEventChatMatrixRoom(roomId),
          })
        }
        chatDebugLog('ensureMatrixChatMembership → done (soft)', {
          chatId,
          roomId,
          joined: true,
        })
        return true
      }

      if (canRunRepair(key)) {
        lastRepairAt.set(key, Date.now())
        pendingForceRepair.delete(key)
        chatDebugLog('ensureMatrixChatMembership → soft miss · retry-structure', {
          chatId,
          roomId,
        })
        return runRetryStructure(chatId, roomId, options.chat, Boolean(options.forceRepair))
      }

      chatDebugLog('ensureMatrixChatMembership → done (soft)', {
        chatId,
        roomId,
        joined: false,
      })
      if (options.isEventChat) {
        eventsDebugLog('membership · failed', {
          chatId,
          roomId,
          matrix: describeEventChatMatrixRoom(roomId),
        })
      }
      return false
    } finally {
      pendingForceRepair.delete(key)
    }
  })().finally(() => {
    if (inFlight.get(key) === promise) {
      inFlight.delete(key)
    }
  })

  inFlight.set(key, promise)
  return promise
}
